import { useState, useRef, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';

import { DefaultDropdown } from '@/component/Dropdown';
import { useListingSearchStore } from '@/store';
import { LocationInput } from './LocationInput';
import { LocationModal } from './LocationModal';
import { LocationDropdown } from '../LocationDropdown';
import { useSearchLocations } from './locations';

import styles from './LocationField.module.css';

export const SearchListingLocationField = ({
  currentStep,
  onSetStep,
  theme,
}) => {
  const {
    setLocation,
    location,
    invalidField,
    setInvalidField,
    setFocusField,
    focusField,
  } = useListingSearchStore();
  const [isVisible, setIsVisible] = useState(false);
  const [value, setValue] = useState(location?.name || '');
  const { data, loading, isEmpty } = useSearchLocations({ value });
  const isMobile = useMediaQuery({ query: '(max-width: 640px)' });
  const isMobileRef = useRef(isMobile);

  const componentRef = useRef();
  const ref = useRef();

  const onChange = (e) => {
    setValue(e.target.value);
    setIsVisible(e.target.value.length > 0);
  };

  const handleInputFocus = () => {
    setFocusField('location');
    setIsVisible(value?.length > 0);
  };

  const handleBlur = (e) => {
    if (
      e.relatedTarget &&
      componentRef.current &&
      !componentRef.current.contains(e.relatedTarget)
    ) {
      setFocusField(null);
      setIsVisible(false);
      return;
    }
    if (!e.relatedTarget) {
      setFocusField(null);
    }
  };

  const onSearchClick = () => {
    if (isMobile && !currentStep) {
      onSetStep('location');
      return;
    }
    if (!isMobile && !isVisible) {
      ref.current?.focus();
    }
    if (!isMobile) {
      document
        .getElementById('search-section')
        .scrollIntoView({ block: 'end' });
    }
  };

  const handleClearValue = (e) => {
    if (e) e.stopPropagation();
    setValue('');
    setLocation();
    setFocusField(null);
    setIsVisible(false);
    onSetStep(null);
  };

  const handleCloseDropdown = (e) => {
    if (e && componentRef.current && componentRef.current.contains(e.target)) {
      return;
    }
    if (e) e.stopPropagation();
    setIsVisible(false);
    setFocusField(null);
  };

  const handleCloseModal = (e) => {
    if (e) e.stopPropagation();
    setFocusField(null);
    setIsVisible(false);
    onSetStep(null);
  };

  const handleNextStep = (e) => {
    e.stopPropagation();
    onSetStep('date');
    setIsVisible(false);
  };

  const onSearchChange = (e) => {
    setValue(e.target.value);
  };

  const handleSelect = (item) => {
    setValue(item.value);
    setLocation(item.data);
    onSetStep('date');
  };

  useEffect(() => {
    if (invalidField === 'location') {
      setInvalidField(null);
      ref.current?.focus();
    }
  }, [invalidField]);

  useEffect(() => {
    if (isMobileRef.current !== isMobile && !isMobile) {
      handleCloseModal();
    }
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffect(() => {
    // HACK: if location was cleared globally we have to reset local state
    if (value && !location?.name) {
      setValue('');
    }
    // otherwise if location was set globally we have to set local state
    if (!value && location?.name) {
      setValue(location?.name);
    }
  }, [location]);

  return (
    <section
      onClick={onSearchClick}
      className={`location ${styles.location} ${
        focusField === 'location' ? '!bg-white !rounded-t-20' : ''
      } ${theme === 'compact' ? styles.compact : ''}
      ${theme === 'vertical' ? styles.vertical : ''}`}
      ref={componentRef}
      onBlur={handleBlur}
    >
      <LocationInput
        className={styles.box}
        value={value}
        onChange={onChange}
        onReset={handleClearValue}
        ref={ref}
        onFocus={handleInputFocus}
        isFocused={focusField === 'location'}
        tabIndex={1}
        theme={theme}
      />

      {isVisible && !isMobile && (
        <DefaultDropdown>
          <LocationDropdown
            closeModal={handleCloseDropdown}
            locationValue={value}
            setValue={setValue}
          />
        </DefaultDropdown>
      )}

      {currentStep === 'location' && isMobile && (
        <LocationModal
          onClose={handleCloseModal}
          onSubmit={handleNextStep}
          search={value}
          onSearchChange={onSearchChange}
          items={data}
          loading={loading}
          isItemsEmpty={isEmpty}
          onSelect={handleSelect}
        />
      )}
    </section>
  );
};
